'use client';
import { analytics } from '@lib/analytics/page-events';
import { signIn } from '@lib/auth';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import type {
  GetProductListsV2Response,
  ProductMembership,
} from '@lib/types/api/ProductListV2';
import { useSession } from 'next-auth/react';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSWRConfig } from 'swr';
import {
  FavoriteListModal,
  SaveToListModal,
  SnackBar,
  type FavoriteListModalFormData,
  type ListData,
} from 'ui-components';

import {
  ProductListsContext,
  type IsProductInListOptions,
} from './ProductListsContext';
import { addItemToList, createList } from './productListsApi';
import { classifyCreateListError } from './productListsErrors';
import {
  buildMembershipAfterSave,
  buildSaveToastContent,
  findSaveForLaterList,
  getListChanges,
  getSavedListsHref,
  syncListChanges,
  toListData,
  trackListChangesAnalytics,
  type CommitListChangesParams,
  type ProductInfo,
} from './productListsHelpers';
import {
  PRODUCT_LISTS_MICROCOPY_DEFAULTS,
  buildChangeModalMicrocopy,
  buildCreateModalMicrocopy,
} from './productListsMicrocopy';
import { useListsData } from './useListsData';
import { useProductMembership } from './useProductMembership';
import { useSaveToListModal } from './useSaveToListModal';
import { useToast } from './useToast';

export const ProductListsProvider = ({ children }: PropsWithChildren) => {
  const { flags, loading: isFlagsLoading } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const isUnauthenticated = status === 'unauthenticated';
  const shouldFetch = isAuthenticated && isProductListsEnabled;
  const isProductListsSettled = !isFlagsLoading && status !== 'loading';

  const mc = useMicrocopy('product-lists', PRODUCT_LISTS_MICROCOPY_DEFAULTS);

  const {
    personalLists,
    orgLists,
    orgListIds,
    publisherOrganization,
    organizationId,
    isOrgSettled,
    isLoading,
    isPersonalListsError,
    mutatePersonalLists,
    mutateOrgLists,
  } = useListsData(shouldFetch);

  const {
    isProductInList: getProductInList,
    getMembership,
    fetchProductMembership,
    fetchBatchProductMembership,
    refreshProductMembership,
    setMembership,
    removeListFromMembershipCache,
  } = useProductMembership(shouldFetch, isOrgSettled, organizationId);

  const savedForLaterListId = findSaveForLaterList(personalLists)?.id;
  const isProductInList = useCallback(
    (
      productId: string,
      { excludeSavedForLater = false }: IsProductInListOptions = {}
    ): boolean | null => {
      if (isProductListsEnabled && isUnauthenticated) return false;
      if (!excludeSavedForLater) return getProductInList(productId);
      // Until the lists arrive, Saved for Later cannot be told apart — and
      // after a failed fetch the stand-in `[]` would count it as a bookmark.
      if (isLoading || isPersonalListsError) return null;
      return getProductInList(productId, savedForLaterListId);
    },
    [
      isProductListsEnabled,
      isUnauthenticated,
      getProductInList,
      isLoading,
      isPersonalListsError,
      savedForLaterListId,
    ]
  );

  const modal = useSaveToListModal();
  const { openSave, reseedSave } = modal;
  const { toast, showToast, dismissToast } = useToast();

  const { mutate: globalMutate } = useSWRConfig();

  const listSlugById = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of personalLists) map.set(list.id, list.slug);
    for (const list of orgLists) map.set(list.id, list.slug);
    return map;
  }, [personalLists, orgLists]);

  const invalidateListItems = useCallback(
    (listIds: string[]) => {
      if (listIds.length === 0) return;
      globalMutate(
        (key) =>
          typeof key === 'string' &&
          listIds.some((id) => {
            const slug = listSlugById.get(id);
            return (
              key.startsWith(`/api/lists/${id}/items`) ||
              (organizationId &&
                key.startsWith(
                  `/api/organizations/${organizationId}/lists/${id}/items`
                )) ||
              (slug &&
                key.startsWith(
                  `/api/lists/by-slug/${encodeURIComponent(slug)}/items`
                ))
            );
          })
      );
    },
    [globalMutate, organizationId, listSlugById]
  );

  const getOrgIdForList = useCallback(
    (listId: string) => (orgListIds.has(listId) ? organizationId : undefined),
    [orgListIds, organizationId]
  );

  const listNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of personalLists) map.set(list.id, list.name);
    for (const list of orgLists) map.set(list.id, list.name);
    return map;
  }, [personalLists, orgLists]);

  const toastMicrocopy = useMemo(
    () => ({
      savedTo: mc['product-lists.saved-to'],
      removedFrom: mc['product-lists.removed-from'],
      multipleLists: mc['product-lists.multiple-lists'],
    }),
    [mc]
  );

  // A resolution can take seconds on a cold cache (the organization gate plus a
  // lookup), long enough for the user to click a second bookmark. Only the
  // newest click may open the dialog.
  const toggleTokenRef = useRef(0);

  const toggleProductList = useCallback(
    async (product: ProductInfo) => {
      if (!isAuthenticated) {
        signIn();
        return;
      }
      const token = ++toggleTokenRef.current;
      // The dialog seeds its selection once, on open, so an unknown membership
      // has to be resolved first. The resolved value is used directly:
      // `getMembership` closes over this render's `data`, stale after the await.
      const membership =
        getProductInList(product.id) === null
          ? await fetchProductMembership(product.id)
          : getMembership(product.id);

      // A later click already opened the dialog for its own product; opening
      // now would remount it seeded for this one, mid-interaction.
      if (token !== toggleTokenRef.current) return;

      // `null` is "still unknown" (the bounded organization wait expired), not
      // "in no lists". Open anyway — a dialog that never opens is worse — but
      // mark the seed provisional so it is replaced rather than saved against.
      openSave(
        product,
        membership ? [...membership.personal, ...membership.organization] : [],
        membership === null
      );
    },
    [
      isAuthenticated,
      getProductInList,
      getMembership,
      fetchProductMembership,
      openSave,
    ]
  );

  const pendingMembershipProductId = modal.isMembershipPending
    ? modal.product?.id
    : undefined;

  useEffect(() => {
    if (!pendingMembershipProductId) return;
    if (getProductInList(pendingMembershipProductId) === null) return;
    const membership = getMembership(pendingMembershipProductId);
    reseedSave([...membership.personal, ...membership.organization]);
  }, [pendingMembershipProductId, getProductInList, getMembership, reseedSave]);

  const refreshPersonalLists = useCallback(
    async () =>
      (await mutatePersonalLists()) as GetProductListsV2Response | undefined,
    [mutatePersonalLists]
  );

  const commitListChanges = useCallback(
    async ({
      product,
      listsToAdd,
      idsToRemove,
      membership,
    }: CommitListChangesParams) => {
      await syncListChanges({
        listsToAdd,
        idsToRemove,
        productId: product.id,
        getOrgIdForList,
      });
      trackListChangesAnalytics({
        listsToAdd,
        idsToRemove,
        productId: product.id,
        productName: product.name,
      });
      if (membership) setMembership(product.id, membership);

      const changedIds = [...listsToAdd.map((l) => l.id), ...idsToRemove];
      // The writes already landed; a failed refresh must not read as a failed save.
      try {
        await mutatePersonalLists();
        if (changedIds.some((id) => orgListIds.has(id))) await mutateOrgLists();
      } catch (error) {
        console.error('Error refreshing lists after save:', error);
      } finally {
        invalidateListItems(changedIds);
      }
    },
    [
      getOrgIdForList,
      setMembership,
      mutatePersonalLists,
      mutateOrgLists,
      orgListIds,
      invalidateListItems,
    ]
  );

  const handleSave = useCallback(
    async (selectedLists: ListData[]) => {
      const { product, initialSelectedListIds, fromCreateFlow } = modal;
      if (!product) return;

      // Claimed before the baseline re-resolution below, which can take a round
      // trip plus the bounded organization wait: leaving Save enabled and
      // spinner-less through that window invites a second click, and a
      // concurrent `handleSave` would duplicate the writes and the analytics.
      modal.setSubmitting(true);
      modal.setServerError(null);

      // The seed is still a placeholder, so the reseed never landed (the lookup
      // failed, or the organization never settled). Diffing against it would
      // re-add lists the product is already in and then pin that incomplete
      // selection as authoritative, so re-attempt the lookup for a real
      // baseline and fall back to the placeholder only if it is still unknown.
      const resolvedBaseline = modal.isMembershipPending
        ? await fetchProductMembership(product.id)
        : null;
      const baselineListIds = resolvedBaseline
        ? [...resolvedBaseline.personal, ...resolvedBaseline.organization]
        : initialSelectedListIds;

      const availableListIds = new Set(listNameById.keys());
      const { listsToAdd, idsToRemove } = getListChanges(
        baselineListIds,
        selectedLists,
        availableListIds
      );

      // The user never saw the resolved baseline — the dialog was seeded empty —
      // so a row they left unchecked means "never shown", not "remove me".
      const removals = resolvedBaseline ? [] : idsToRemove;

      // Nothing to persist (e.g. confirming an inline create) — just toast.
      if (listsToAdd.length === 0 && removals.length === 0) {
        if (fromCreateFlow) {
          if (modal.createdListName) {
            showToast(
              mc['product-lists.saved-to'],
              modal.createdListName,
              getSavedListsHref([modal.createdListSlug ?? undefined])
            );
          } else {
            showToast(mc['product-lists.saved']);
          }
        }
        modal.close();
        return;
      }

      try {
        const savedMembership = buildMembershipAfterSave(
          getMembership(product.id),
          selectedLists,
          availableListIds
        );
        // `buildMembershipAfterSave` treats the selection as the whole truth,
        // but a placeholder-seeded save can only add — union the resolved
        // baseline back in so lists the user never saw survive in the cache.
        const newMembership: ProductMembership = resolvedBaseline
          ? {
              personal: Array.from(
                new Set([
                  ...savedMembership.personal,
                  ...resolvedBaseline.personal,
                ])
              ),
              organization: Array.from(
                new Set([
                  ...savedMembership.organization,
                  ...resolvedBaseline.organization,
                ])
              ),
            }
          : savedMembership;
        await commitListChanges({
          product,
          listsToAdd,
          idsToRemove: removals,
          membership: newMembership,
        });

        const toastContent = buildSaveToastContent({
          addedNames: listsToAdd.map((l) => l.name),
          removedNames: removals.map((id) => listNameById.get(id) ?? id),
          microcopy: toastMicrocopy,
        });
        const addedHref =
          listsToAdd.length > 0
            ? getSavedListsHref(listsToAdd.map((l) => listSlugById.get(l.id)))
            : undefined;
        if (toastContent)
          showToast(toastContent.title, toastContent.listLabel, addedHref);
        else showToast(mc['product-lists.saved']);
        modal.close();
      } catch (error) {
        console.error('Error syncing list changes:', error);
        modal.setSubmitting(false);
        modal.setServerError('network');
      }
    },
    [
      modal,
      getMembership,
      fetchProductMembership,
      commitListChanges,
      listNameById,
      listSlugById,
      toastMicrocopy,
      showToast,
      mc,
    ]
  );

  const createListAndAddProduct = useCallback(
    async (formData: FavoriteListModalFormData) => {
      const { product } = modal;
      if (!product) return;

      const isOrgList = formData.accountType === 'organization';
      const targetOrgId = isOrgList ? organizationId : undefined;

      modal.setSubmitting(true);
      modal.setServerError(null);
      try {
        const newList = await createList(
          formData.listName,
          formData.visibility,
          targetOrgId
        );
        analytics.pushCreateListEvent({
          list_id: newList.id,
          list_name: formData.listName,
          list_visibility: formData.visibility,
          list_type: isOrgList ? 'organization' : 'personal',
          product_id: product.id,
          product_name: product.name,
          organization_id: targetOrgId,
        });

        await addItemToList(newList.id, product.id, targetOrgId);
        analytics.pushSaveToListEvent({
          asset_id: product.id,
          asset_name: product.name,
        });

        if (isOrgList) await mutateOrgLists();
        else await mutatePersonalLists();
        invalidateListItems([newList.id]);

        const prev = getMembership(product.id);
        const newMembership: ProductMembership = isOrgList
          ? {
              personal: prev.personal,
              organization: [...prev.organization, newList.id],
            }
          : {
              personal: [...prev.personal, newList.id],
              organization: prev.organization,
            };
        setMembership(product.id, newMembership);

        modal.returnToSaveAfterCreate(
          [...newMembership.personal, ...newMembership.organization],
          newList.name,
          newList.slug
        );
      } catch (error) {
        console.error('Error creating list and adding product:', error);
        modal.setSubmitting(false);
        modal.setServerError(classifyCreateListError(error));
      }
    },
    [
      modal,
      organizationId,
      mutateOrgLists,
      mutatePersonalLists,
      invalidateListItems,
      getMembership,
      setMembership,
    ]
  );

  const createModalMicrocopy = useMemo(
    () => buildCreateModalMicrocopy(mc),
    [mc]
  );
  const changeModalMicrocopy = useMemo(
    () => buildChangeModalMicrocopy(mc, modal.fromCreateFlow),
    [mc, modal.fromCreateFlow]
  );

  const value = useMemo(
    () => ({
      personalLists,
      orgLists,
      isLoading,
      isProductInList,
      toggleProductList,
      fetchProductMembership,
      fetchBatchProductMembership,
      removeListFromMembershipCache,
      isProductListsEnabled,
      isProductListsSettled,
      refreshPersonalLists,
      refreshProductMembership,
      commitListChanges,
    }),
    [
      personalLists,
      orgLists,
      isLoading,
      isProductInList,
      toggleProductList,
      fetchProductMembership,
      fetchBatchProductMembership,
      removeListFromMembershipCache,
      isProductListsEnabled,
      isProductListsSettled,
      refreshPersonalLists,
      refreshProductMembership,
      commitListChanges,
    ]
  );

  const toastHref = toast?.href;
  const toastListLabel = toast?.listLabel;

  return (
    <ProductListsContext.Provider value={value}>
      {children}

      {toast && (
        <SnackBar
          key={toast.key}
          icon="BookmarkSimple"
          iconWeight="fill"
          closeIcon="X"
          onClose={dismissToast}
          {...(toastHref && {
            actionIcon: 'ArrowSquareOut',
            onActionClick: () => window.open(toastHref, '_blank', 'noopener'),
          })}
          microcopy={{
            title: toast.title,
            ...(toastListLabel ? { actionLabel: toastListLabel } : {}),
            ...(toastHref &&
              toastListLabel && {
                actionAriaLabel: mc['product-lists.opens-in-new-tab'].replace(
                  '{label}',
                  () => toastListLabel
                ),
              }),
            closeAriaLabel: mc['product-lists.close'],
          }}
        />
      )}

      <FavoriteListModal
        open={modal.view === 'create'}
        onOpenChange={(open) => {
          if (!open) modal.close();
        }}
        listName=""
        visibility="private"
        accountType={organizationId ? modal.createAccountType : 'personal'}
        showAccountType={!!organizationId}
        userName={session?.user?.name ?? ''}
        userProfilePicture={session?.user?.image}
        publisherName={publisherOrganization?.name ?? ''}
        microcopy={createModalMicrocopy}
        onSave={createListAndAddProduct}
        isSubmitting={modal.isSubmitting}
        position="bottom"
        showBackdrop={false}
        serverError={modal.serverError !== null}
        limitReached={modal.serverError === 'limit'}
        onDismissError={() => modal.setServerError(null)}
      />

      {modal.view === 'save' && (
        <SaveToListModal
          key={modal.openToken}
          open
          onOpenChange={(open) => {
            if (!open) modal.close();
          }}
          list={[
            ...personalLists.map((l) => toListData(l, true)),
            ...orgLists.map((l) => toListData(l, false)),
          ]}
          multiTabs={!!organizationId}
          initialSelectedListIds={modal.initialSelectedListIds}
          forceEnableSave={modal.fromCreateFlow}
          userName={session?.user?.name ?? ''}
          userProfilePicture={session?.user?.image}
          publisherName={publisherOrganization?.name ?? ''}
          microcopy={changeModalMicrocopy}
          onNewList={modal.openCreate}
          onSave={handleSave}
          isSubmitting={modal.isSubmitting}
          position="bottom"
          showBackdrop={false}
          serverError={modal.serverError !== null}
          onDismissError={() => modal.setServerError(null)}
        />
      )}
    </ProductListsContext.Provider>
  );
};
